import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { RECORD_KEY, type CorrespondenceOut, type CustomerOut } from "@/api";
import { Button } from "@/components/Button";
import { Dialog, DialogFooter } from "@/components/Dialog";
import { CustomerFromMail } from "@/features/customer/CustomerFromMail";

/** What is said, aloud, once the customer stands — the block's own sentence, which goes with the dialog. */
export function customerAssignedSentence(customer: CustomerOut): string {
  return `„${customer.company ?? customer.displayName}“ ist jetzt der Kunde dieses Vorgangs.`;
}

interface CustomerFromMailDialogProps {
  caseId: string;
  /** The mail the customer is taken from — an incoming or outgoing mail of the record. */
  entry: CorrespondenceOut;
  onClose: () => void;
  /** The customer is the Vorgang's now; the dialog is closing. */
  onDone: (customer: CustomerOut) => void;
}

/**
 * „Kunde aus dieser Mail“ in the record (spec 2026-10-20 §3.3): the same
 * building block the Eingang shows, in a dialog — so a mail that is handed in
 * later, or was filed before this existed, gets its customer the same way.
 *
 * The block is reused whole (`CustomerFromMail`): it reads the mail, lists
 * the customers it might be, assigns nothing without a press and saves the
 * contact person with the customer. This dialog is only its frame: the title
 * says which mail; „Schließen“ is the way out the block does not have (it has
 * its own „Abbrechen“ only where it goes back a step); Escape and „Schließen“
 * are held while a request is out (its answer — a 409 for a Vorgang cancelled
 * meanwhile — would be lost with the dialog); and once the customer stands
 * the dialog closes and the record is read again, so the fact sheet names
 * them. The block alone has already refreshed `CASE_KEY`; the record is asked
 * by name as well, so this does not depend on that key being the prefix of
 * the other.
 */
export function CustomerFromMailDialog({ caseId, entry, onClose, onDone }: CustomerFromMailDialogProps) {
  const client = useQueryClient();
  const [busy, setBusy] = useState(false);

  function close() {
    if (!busy) onClose();
  }

  return (
    <Dialog
      title="Kunde aus dieser Mail"
      subtitle={entry.subject ?? entry.sourceFilename ?? "Mail ohne Betreff"}
      onClose={close}
      busy={busy}
    >
      <CustomerFromMail
        caseId={caseId}
        entryId={entry.id}
        headingLevel={3}
        contactFrom="mail"
        onBusy={setBusy}
        onDone={(customer) => {
          void client.invalidateQueries({ queryKey: RECORD_KEY(caseId) });
          if (customer !== null) onDone(customer);
          else onClose();
        }}
      />
      <DialogFooter>
        <Button aria-disabled={busy ? "true" : undefined} onClick={close}>
          Schließen
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
